import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Layers, Send, Archive } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import ProcessStepCard from './ProcessStepCard';

function WorkOrderTreeItem({ order, onBatchAssign, isHistorical }) {
  const [expanded, setExpanded] = useState(false);

  const totalSteps = order.items?.reduce((sum, item) => sum + (item.steps?.length || 0), 0) || 0;
  const subcontractedSteps = order.items?.reduce(
    (sum, item) => sum + (item.steps?.filter((s) => s.is_subcontracted).length || 0),
    0
  ) || 0;
  const assignableSteps = totalSteps - subcontractedSteps;

  const priorityColors = {
    urgent: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    normal: 'bg-blue-100 text-blue-700',
    low: 'bg-gray-100 text-gray-700',
  };

  return (
    <div className="mb-2">
      <div
        className="flex items-center gap-1.5 px-2 py-2 bg-gray-50 rounded-md cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-gray-500 shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-gray-500 shrink-0" />
        )}
        {isHistorical ? (
          <Archive className="h-3.5 w-3.5 text-gray-400 shrink-0" />
        ) : (
          <Layers className="h-3.5 w-3.5 text-gray-500 shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-xs font-semibold text-gray-800 truncate">
              {order.production_order_number}
            </span>
            <Badge
              variant="secondary"
              className={`text-[9px] h-4 px-1 ${priorityColors[order.priority] || priorityColors.normal}`}
            >
              {order.priority === 'urgent' ? '紧急' : order.priority === 'high' ? '高' : order.priority === 'low' ? '低' : '普通'}
            </Badge>
            {isHistorical && order.status !== 'in_progress' && (
              <Badge variant="outline" className="text-[9px] h-4 px-1 text-gray-500">
                {order.status === 'completed' ? '已完成' : order.status === 'cancelled' ? '已取消' : order.status}
              </Badge>
            )}
          </div>
          <div className="text-[10px] text-gray-500 truncate">
            {order.client_name} · {order.items?.length || 0}个产品 · {assignableSteps}个待分配工序
          </div>
        </div>
        {assignableSteps > 0 && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={(e) => {
              e.stopPropagation();
              onBatchAssign?.(order);
            }}
            title="全部分配"
          >
            <Send className="h-3 w-3 text-blue-500" />
          </Button>
        )}
      </div>

      {expanded && (
        <div className="mt-1 ml-4 space-y-2">
          {order.items?.map((item) => (
            <div key={item.item_id} className="border-l-2 border-gray-200 pl-2">
              <div className="text-[11px] font-medium text-gray-700 mb-1">
                <span className="text-blue-600 font-semibold">
                  {order.production_order_number}-{String(item.item_index + 1).padStart(3, '0')}
                </span>
                <span className="text-gray-300 mx-1">·</span>
                {item.finished_product_code}
                {item.finished_product_name && ` · ${item.finished_product_name}`}
                {item.specification && (
                  <span className="text-amber-600 font-medium ml-1">({item.specification})</span>
                )}
                <span className="text-gray-400 font-normal ml-1">
                  ({item.planned_quantity}件)
                </span>
              </div>
              <div className="space-y-0.5">
                {item.steps?.map((step) => (
                  <ProcessStepCard
                    key={`${order.production_order_id}-${item.item_id}-${step.sequence}`}
                    step={step}
                    productName={item.finished_product_name}
                    productCode={item.finished_product_code}
                    specification={item.specification}
                    plannedQuantity={item.planned_quantity}
                    completedQuantity={step.quantity_completed}
                    remainingQuantity={step.remaining_quantity}
                    orderId={order.production_order_id}
                    orderNumber={order.production_order_number}
                    itemId={item.item_id}
                    itemIndex={item.item_index}
                    clientName={order.client_name}
                    isSubcontracted={step.is_subcontracted}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default WorkOrderTreeItem;
